import { createContext, useContext } from "react";
import type { CheckoutComponent } from "../interfaces/base";
import type {
  CheckoutContext,
  CheckoutDeliveryMethod,
} from "../utilities/checkoutTypes";
import { isDeliveryLocationMethod } from "./deliveryParcelMachine";

export type CheckoutDeliverySource = "from_order_products" | "fixed";

export type CheckoutDeliveryConfig = {
  visible: boolean;
  source: CheckoutDeliverySource;
  fixedDeliveryMethodUuid?: string | null;
};

export type CheckoutPaymentConfig = {
  visible: boolean;
};

export type CheckoutLegalLinksConfig = {
  termsUrl?: string;
  cookiePolicyUrl?: string;
  privacyPolicyUrl?: string;
};

export type CheckoutB2bContactPersonConfig = {
  visible: boolean;
  required: boolean;
};

export type CheckoutConfig = {
  delivery: CheckoutDeliveryConfig;
  payment: CheckoutPaymentConfig;
  legalLinks: CheckoutLegalLinksConfig;
  b2bContactPerson: CheckoutB2bContactPersonConfig;
  confirmOrderButtonText?: string;
};

export const defaultCheckoutDeliveryConfig: CheckoutDeliveryConfig = {
  visible: true,
  source: "from_order_products",
};

export const defaultCheckoutPaymentConfig: CheckoutPaymentConfig = {
  visible: true,
};

export const defaultCheckoutLegalLinksConfig: CheckoutLegalLinksConfig = {};

export const defaultCheckoutB2bContactPersonConfig: CheckoutB2bContactPersonConfig =
  {
    visible: false,
    required: true,
  };

export const defaultCheckoutConfig: CheckoutConfig = {
  delivery: defaultCheckoutDeliveryConfig,
  payment: defaultCheckoutPaymentConfig,
  legalLinks: defaultCheckoutLegalLinksConfig,
  b2bContactPerson: defaultCheckoutB2bContactPersonConfig,
};

function parseOptionalUrl(value: string | null | undefined): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function parseCheckoutLegalLinksConfig(
  attributes: CheckoutComponent["attributes"] | undefined
): CheckoutLegalLinksConfig {
  return {
    termsUrl: parseOptionalUrl(attributes?.terms_url),
    cookiePolicyUrl: parseOptionalUrl(attributes?.cookie_policy_url),
    privacyPolicyUrl: parseOptionalUrl(attributes?.privacy_policy_url),
  };
}

export function parseCheckoutDeliveryConfig(
  attributes: CheckoutComponent["attributes"] | undefined
): CheckoutDeliveryConfig {
  const source = attributes?.delivery_method_source ?? "from_order_products";
  return {
    visible: attributes?.delivery_method !== "hidden",
    source,
    fixedDeliveryMethodUuid:
      source === "fixed" ? attributes?.fixed_delivery_method_uuid : undefined,
  };
}

function parseOptionalText(
  value: string | null | undefined
): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function parseCheckoutConfig(
  attributes: CheckoutComponent["attributes"] | undefined
): CheckoutConfig {
  return {
    delivery: parseCheckoutDeliveryConfig(attributes),
    payment: {
      visible: attributes?.payment_method !== "hidden",
    },
    legalLinks: parseCheckoutLegalLinksConfig(attributes),
    b2bContactPerson: {
      visible: attributes?.b2b_contact_person === "visible",
      required: attributes?.b2b_contact_person_required !== "optional",
    },
    confirmOrderButtonText: parseOptionalText(
      attributes?.confirm_order_button_text
    ),
  };
}

export function getConfirmOrderButtonText(
  locale: string,
  config: CheckoutConfig
): string {
  const custom = config.confirmOrderButtonText?.trim();
  if (custom) {
    return custom;
  }
  return locale === "en" ? "SUBMIT ORDER" : "KINNITA TELLIMUS";
}

export function getSyncOrderCheckoutContext(
  config: CheckoutDeliveryConfig
): CheckoutContext | undefined {
  if (
    config.source !== "fixed" ||
    typeof config.fixedDeliveryMethodUuid === "undefined"
  ) {
    return undefined;
  }
  return {
    mode: "fixed",
    deliveryMethodUuid: config.fixedDeliveryMethodUuid,
  };
}

export function isDeliveryDisabledByFixedNone(
  config: CheckoutDeliveryConfig
): boolean {
  return config.source === "fixed" && config.fixedDeliveryMethodUuid === null;
}

export const CheckoutConfigContext = createContext<CheckoutConfig>(
  defaultCheckoutConfig
);

export function useCheckoutConfig(): CheckoutConfig {
  return useContext(CheckoutConfigContext);
}

export function getCheckoutStepNumbers(config: CheckoutConfig) {
  const deliveryStepVisible =
    config.delivery.visible &&
    !isDeliveryDisabledByFixedNone(config.delivery);

  let step = deliveryStepVisible ? 4 : 3;
  const result: { delivery?: number; payment?: number; confirm: number } = {
    confirm: step,
  };

  if (deliveryStepVisible) {
    result.delivery = 3;
  }

  if (config.payment.visible) {
    result.payment = step;
  }

  result.confirm = step;
  return result;
}

/** Payment step number for modals; always returned even when the inline block is hidden. */
export function getPaymentStepNumber(config: CheckoutConfig): number {
  const deliveryStepVisible =
    config.delivery.visible &&
    !isDeliveryDisabledByFixedNone(config.delivery);
  return deliveryStepVisible ? 4 : 3;
}

export function filterDeliveryMethodsForConfig<
  T extends Pick<CheckoutDeliveryMethod, "uuid">,
>(methods: T[], config: CheckoutDeliveryConfig): T[] {
  if (config.source !== "fixed") {
    return methods;
  }

  if (config.fixedDeliveryMethodUuid === null) {
    return [];
  }

  if (typeof config.fixedDeliveryMethodUuid === "undefined") {
    return methods;
  }

  return methods.filter((m) => m.uuid === config.fixedDeliveryMethodUuid);
}

/** Methods that can be auto-applied without opening the delivery details modal. */
export function isSimpleDeliveryMethod(
  method: Pick<
    CheckoutDeliveryMethod,
    | "methodCode"
    | "timingRequired"
    | "deliveryMethodTimings"
    | "getDeliveryLocationsUrl"
  >
): boolean {
  if (method.methodCode === "store_pickup") {
    return !(method.timingRequired || method.deliveryMethodTimings?.length);
  }
  if (method.timingRequired) {
    return false;
  }
  if (method.deliveryMethodTimings?.length) {
    return false;
  }
  if (isDeliveryLocationMethod(method.methodCode)) {
    return false;
  }
  if (method.getDeliveryLocationsUrl) {
    return false;
  }
  if (method.methodCode === "courier") {
    return false;
  }
  return false;
}

export function isMethodUuidInList<T extends { uuid: string }>(
  methods: T[],
  uuid?: string | null
): boolean {
  if (!uuid) {
    return false;
  }
  return methods.some((method) => method.uuid === uuid);
}

export function resolveSelectedPaymentMethod<T extends { uuid: string }>(
  paymentMethod: T | null | undefined,
  paymentMethods: T[]
): T | null {
  if (!paymentMethod?.uuid) {
    return null;
  }
  return (
    paymentMethods.find((method) => method.uuid === paymentMethod.uuid) ?? null
  );
}

export type OrderDeliverySelection = {
  uuid?: string | null;
  requestedDeliveryDateTime?: string | null;
  estimatedDeliveryDateTime?: string | null;
};

export function isOrderDeliverySelectionComplete(
  orderDelivery: OrderDeliverySelection | null | undefined,
  deliveryMethod: CheckoutDeliveryMethod | null | undefined
): boolean {
  if (!orderDelivery?.uuid) {
    return false;
  }
  if (!deliveryMethod) {
    return false;
  }

  const requiresDateTime =
    !!deliveryMethod.timingRequired ||
    !!deliveryMethod.deliveryMethodTimings?.length;
  if (
    requiresDateTime &&
    !orderDelivery.requestedDeliveryDateTime &&
    !orderDelivery.estimatedDeliveryDateTime
  ) {
    return false;
  }

  return true;
}
